import React from "react";
import { View } from "react-native";
import { Feather } from "@expo/vector-icons";
import { Color } from "@tiptap/extension-color";
import TipTapImage from "@tiptap/extension-image";
import ListItem from "@tiptap/extension-list-item";
import TipTapPlaceholder from "@tiptap/extension-placeholder";
import TextStyle from "@tiptap/extension-text-style";
import TipTapUnderline from "@tiptap/extension-underline";
import {
  EditorProvider,
  generateHTML,
  generateText,
  JSONContent,
  useCurrentEditor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { isEmpty } from "lodash";

import {
  useGetShouldUpdateFieldForSaveForm,
  useSaveFormContext,
} from "~/components/SaveForm/shared";
// import { useGetShouldUpdateField } from "~/hooks/useShouldUpdateField";
// import { common, createLowlight } from "lowlight";

import { cn } from "~/lib/utils";
import { Button } from "~/ui/Button";
import { Dropdown } from "~/ui/Dropdown";

// const lowlight = createLowlight(common);
const MenuBar = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <div className="control-group">
      <View className="button-group flex-row items-center gap-2">
        <Dropdown
          trigger={
            <Button
              variant="outline"
              size="small"
              iconAfter={<Feather name="chevron-down" size={16} />}
            >
              {(() => {
                if (editor.isActive("heading")) {
                  if (editor.getAttributes("heading").level === 1)
                    return "Heading 1";
                  if (editor.getAttributes("heading").level === 2)
                    return "Heading 2";
                  if (editor.getAttributes("heading").level === 3)
                    return "Heading 3";
                  if (editor.getAttributes("heading").level === 4)
                    return "Heading 4";
                }
                return "Normal";
              })()}
            </Button>
          }
          groups={[
            {
              items: [
                {
                  key: "normal",
                  title: "Normal",
                  onSelect() {
                    editor.chain().focus().setParagraph().run();
                  },
                },
              ],
            },
            {
              items: ([1, 2, 3, 4] as const).map((level) => ({
                key: `Heading ${level}`,
                title: `Heading ${level}`,
                onSelect() {
                  editor.chain().focus().toggleHeading({ level }).run();
                },
              })),
            },
          ]}
        />

        <View className="border-input-border flex-row gap-0.5 overflow-hidden rounded-lg border">
          {(["bold", "italic", "underline", "code"] as const).map((mark) => (
            <Button
              key={mark}
              variant="outline"
              size="small"
              className={cn(
                "rounded-none border-0",
                editor.isActive(mark) ? "bg-button-secondarySheet" : "",
              )}
              iconBefore={
                <Feather
                  name={(() => {
                    if (mark === "bold") return "bold";
                    if (mark === "italic") return "italic";
                    if (mark === "underline") return "underline";
                    if (mark === "code") return "code";
                  })()}
                  size={16}
                />
              }
              onPress={() => {
                editor.chain().focus().toggleMark(mark).run();
              }}
            />
          ))}
        </View>

        <View className="border-input-border flex-row gap-0 overflow-hidden rounded-lg border">
          {(
            ["orderedList", "bulletList", "blockquote", "codeBlock"] as const
          ).map((block) => (
            <Button
              key={block}
              variant="outline"
              size="small"
              className={cn(
                "rounded-none border-0",
                editor.isActive(block) ? "bg-button-secondarySheet" : "",
              )}
              iconBefore={(() => {
                if (block === "blockquote")
                  return (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="1em"
                      height="1em"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        fillRule="evenodd"
                        d="M7 21a4 4 0 0 1-4-4c0-1.473 1.333-6.14 4-14h2L7 13a4 4 0 1 1 0 8m10 0a4 4 0 0 1-4-4c0-1.473 1.333-6.14 4-14h2l-2 10a4 4 0 1 1 0 8"
                      ></path>
                    </svg>
                  );

                if (block === "orderedList")
                  return (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="1em"
                      height="1em"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        fillRule="evenodd"
                        d="M10 4h10a1 1 0 0 1 0 2H10a1 1 0 1 1 0-2m0 7h10a1 1 0 0 1 0 2H10a1 1 0 0 1 0-2m0 7h10a1 1 0 0 1 0 2H10a1 1 0 0 1 0-2M4.5 4a.5.5 0 0 1 0-1h1c.28 0 .5.22.5.5v3a.5.5 0 0 1-1 0V4.015zm0 6h2c.28 0 .5.22.5.5v.5l-1.7 2h1.2c.28 0 .5.22.5.5s-.224.5-.5.5h-2c-.28 0-.5-.22-.5-.5V13l1.7-2H4.5c-.28 0-.5-.22-.5-.5s.199-.5.5-.5m2 11h-2a.5.5 0 1 1 0-1H6v-.5H4.5a.5.5 0 1 1 0-1H6V18H4.5a.5.5 0 1 1 0-1h2a.5.5 0 0 1 .5.5v3a.5.5 0 0 1-.5.5"
                      ></path>
                    </svg>
                  );

                return (
                  <Feather
                    name={(() => {
                      if (block === "bulletList") return "list";
                      if (block === "codeBlock") return "code";
                    })()}
                    size={16}
                  />
                );
              })()}
              onPress={() => {
                switch (block) {
                  case "orderedList":
                    return editor.chain().focus().toggleOrderedList().run();
                  case "bulletList":
                    return editor.chain().focus().toggleBulletList().run();
                  case "blockquote":
                    return editor.chain().focus().toggleBlockquote().run();
                  case "codeBlock":
                    return editor.chain().focus().toggleCodeBlock().run();
                }
              }}
            />
          ))}
        </View>

        {/* <button onClick={() => editor.chain().focus().unsetAllMarks().run()}>
          Clear marks
        </button>
        <button onClick={() => editor.chain().focus().clearNodes().run()}>
          Clear nodes
        </button> */}

        <Button
          variant="outline"
          size="small"
          onPress={() => editor.chain().focus().setHorizontalRule().run()}
          iconBefore={<Feather name="minus" size={16} />}
        />

        <Button
          variant="outline"
          size="small"
          onPress={() => {
            const url = prompt("Enter the URL", "https://");
            if (!url) return;
            editor.chain().focus().setImage({ src: url }).run();
          }}
          iconBefore={<Feather name="image" size={16} />}
        />

        <View className="border-input-border ml-auto flex-row gap-0.5 overflow-hidden rounded-lg border">
          {(["undo", "redo"] as const).map((x) => (
            <Button
              key={x}
              variant="outline"
              size="small"
              className={cn("rounded-none border-0")}
              iconBefore={
                <Feather
                  name={(() => {
                    if (x === "undo") return "rotate-ccw";
                    if (x === "redo") return "rotate-cw";
                  })()}
                  size={16}
                />
              }
              onPress={() => {
                switch (x) {
                  case "undo":
                    return editor.chain().focus().undo().run();
                  case "redo":
                    return editor.chain().focus().redo().run();
                }
              }}
            />
          ))}
        </View>
      </View>
    </div>
  );
};

const extensions = [
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle.configure({}),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
    },
    horizontalRule: {
      HTMLAttributes: {
        class: "border-t border-input-border my-4",
      },
    },
    blockquote: {},
    bold: {},
    italic: {},
    code: {},

    codeBlock: {},
    heading: {},
    hardBreak: {},
    strike: {},
    paragraph: {},
  }),
  TipTapImage,
  TipTapUnderline,
  TipTapPlaceholder.configure({
    placeholder: "Write something...",
  }),
];

// TODO: Add link pasting support
// TODO: Add image pasting support
export const AddNote = () => {
  const { setValue, getValues } = useSaveFormContext();
  const { shouldUpdateField } = useGetShouldUpdateFieldForSaveForm();

  // yh this isn't the best, it _should_ be handled when user clicks `continue`, but, user can skip that and directly go to next step
  React.useEffect(() => {
    return () => {
      const richTextAST = getValues("richTextAST");
      if (shouldUpdateField("title") && !isEmpty(richTextAST)) {
        setValue("title", generateText(richTextAST, extensions).split("\n")[0]);
      }
    };
  }, []);
  return (
    <EditorProvider
      editorProps={{
        attributes: {
          class:
            "bg-input-background-active transition-[background] focus:bg-surface-primary p-4 rounded focus-within:outline-none min-h-[300px]",
        },
      }}
      slotBefore={<MenuBar />}
      extensions={extensions}
      content={getValues("richTextAST") ?? null}
      onUpdate={({ editor }) => {
        setValue("richTextAST", editor.getJSON());
      }}
    />
  );
};

export const getTiptapHTML = (doc: JSONContent) =>
  generateHTML(doc, extensions);

export const getTiptapText = (doc: JSONContent) =>
  generateText(doc, extensions);
